// client/src/world/flora/legacy.ts
/**
 * Legacy flora & rock builders — real silhouettes instead of cones.
 *
 * Every variant is a pure function of (planet seed, species, index): the
 * same trees grow in the same shapes on every visit, for every player,
 * forever — persistence through determinism, no data stored. A planet
 * builds 4 variants per species once at load (a few ms), and the scatter
 * system instances them by the hundreds.
 *
 * Construction: a tiny recursive branch skeleton is tubed with low-poly
 * cylinders, canopies are displaced icosphere blobs, and everything is
 * merged into ONE non-indexed, flat-shaded, vertex-colored geometry per
 * variant — bark and leaves in a single draw, tinted per instance.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { rngFromString, range, type Rng, type PlanetDef } from "@hyperlight/shared";
import { V, tube, blob, cone, shade, flat, paint } from "./primitives";
import type { C3 } from "./types";
import { buildBoulder } from "./forestForms";
import { landscapeColors } from "../landscapePalette";
import { TREE_STEM, NO_JOINT, paintTreeWind, windJoint, type WindJoint } from './treeWind';

export type FloraKind = "conifer" | "broadleaf" | "cactus" | "dead" | "crystal";

/** Which species a planet grows, by archetype. Empty = rocks only. */
export function speciesFor(def: PlanetDef): FloraKind[] {
  switch (def.archetype) {
    case "terran":
    case "ocean":
      return []; // the species FloraLibrary owns these worlds now
    case "desert":
      return ["cactus", "dead"];
    case "ice":
      return ["crystal", "conifer"];
    case "volcanic":
      return ["dead", "crystal"];
    default:
      return [];
  }
}

function buildOne(rng: Rng, kind: FloraKind, bark: C3, leaf: C3): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  if (kind === "conifer") {
    // Straight spine, stacked foliage tiers narrowing to a tip.
    const h = range(rng, 5, 8.5);
    parts.push(tube(V(0, 0, 0), V(range(rng, -0.2, 0.2), h * 0.35, range(rng, -0.2, 0.2)), 0.32, 0.2, bark));
    const tiers = 3 + (rng() > 0.5 ? 1 : 0);
    for (let t = 0; t < tiers; t++) {
      const f = t / tiers;
      const y = h * (0.18 + 0.72 * f);
      const r = (1 - f * 0.72) * range(rng, 1.5, 2.1);
      parts.push(cone(y, r, h * 0.34, shade(leaf, range(rng, 0.82, 1) - f * 0.08), 6, 0.7));
    }
  } else if (kind === "broadleaf") {
    // Forking trunk; canopy blobs clustered at the branch tips.
    const trunkH = range(rng, 2.2, 3.6);
    const top = V(range(rng, -0.3, 0.3), trunkH, range(rng, -0.3, 0.3));
    parts.push(tube(V(0, 0, 0), top, 0.34, 0.24, bark));
    const branches = 2 + Math.floor(rng() * 2);
    const tips: THREE.Vector3[] = [top.clone()];
    for (let b = 0; b < branches; b++) {
      const a = (b / branches) * Math.PI * 2 + rng() * 1.2;
      const tip = top
        .clone()
        .add(V(Math.cos(a) * range(rng, 0.9, 1.8), range(rng, 0.7, 1.5), Math.sin(a) * range(rng, 0.9, 1.8)));
      parts.push(tube(top, tip, 0.18, 0.1, bark));
      tips.push(tip);
    }
    for (const tip of tips) {
      parts.push(blob(rng, tip.clone().add(V(0, 0.4, 0)), range(rng, 1.1, 1.7), shade(leaf, range(rng, 0.8, 1.05)), 0.22, 0, 1));
    }
  } else if (kind === "cactus") {
    // Column with elbowed arms; all flesh-colored.
    const h = range(rng, 3, 5);
    const lean = V(range(rng, -0.15, 0.15), h, range(rng, -0.15, 0.15));
    parts.push(tube(V(0, 0, 0), lean, 0.42, 0.3, leaf, 7));
    const arms = Math.floor(rng() * 3);
    for (let i = 0; i < arms; i++) {
      const ay = h * range(rng, 0.3, 0.6);
      const a = rng() * Math.PI * 2;
      const out = V(Math.cos(a), 0, Math.sin(a));
      const elbow = V(out.x * 0.9, ay, out.z * 0.9);
      parts.push(tube(V(out.x * 0.3, ay, out.z * 0.3), elbow, 0.2, 0.2, leaf, 6));
      parts.push(tube(elbow, V(elbow.x, ay + range(rng, 0.8, 1.6), elbow.z), 0.2, 0.15, leaf, 6));
    }
  } else if (kind === "dead") {
    // Twisted bare branches, recursive, no foliage.
    const grow = (a: THREE.Vector3, dir: THREE.Vector3, len: number, r: number, depth: number,
      parent: WindJoint = TREE_STEM, twig: WindJoint = NO_JOINT): void => {
      const b = a.clone().addScaledVector(dir, len);
      const joint = depth === 2 ? windJoint(a, b, r) : parent;
      const terminal = depth === 1 ? windJoint(a, b, r) : twig;
      parts.push(paintTreeWind(tube(a, b, r, r * 0.55, bark), joint, terminal));
      if (depth <= 0 || r < 0.05) return;
      const kids = 1 + Math.floor(rng() * 2);
      for (let i = 0; i < kids; i++) {
        const nd = dir
          .clone()
          .add(V(range(rng, -0.8, 0.8), range(rng, 0.1, 0.7), range(rng, -0.8, 0.8)))
          .normalize();
        grow(b, nd, len * range(rng, 0.55, 0.75), r * 0.55, depth - 1, joint, terminal);
      }
    };
    grow(V(0, 0, 0), V(range(rng, -0.15, 0.15), 1, range(rng, -0.15, 0.15)).normalize(), range(rng, 1.6, 2.6), 0.26, 3);
  } else {
    // crystal: a cluster of tilted, elongated shards.
    const shards = 3 + Math.floor(rng() * 3);
    for (let i = 0; i < shards; i++) {
      const r = range(rng, 0.3, 0.7);
      const geo = flat(new THREE.OctahedronGeometry(r, 0));
      geo.scale(1, range(rng, 2.2, 4.2), 1);
      const tilt = new THREE.Quaternion().setFromEuler(
        new THREE.Euler(range(rng, -0.45, 0.45), rng() * Math.PI * 2, range(rng, -0.45, 0.45)),
      );
      geo.applyQuaternion(tilt);
      geo.translate(range(rng, -0.8, 0.8), r * range(rng, 1.4, 2.4), range(rng, -0.8, 0.8));
      parts.push(paint(geo, shade(leaf, range(rng, 0.85, 1.25))));
    }
  }

  const merged = mergeGeometries(parts, false)!;
  for (const p of parts) p.dispose();
  merged.computeVertexNormals(); // non-indexed soup → true flat shading
  // Retained ice/desert silhouettes have no authored foliage joints. Keep
  // their connected shape under the same restrained whole-stem response.
  if (kind !== 'crystal' && !merged.hasAttribute('branchWind')) paintTreeWind(merged);
  return merged;
}

export interface FloraSet {
  kind: FloraKind;
  variants: THREE.BufferGeometry[];
}

/** Deterministic per-planet flora library: 4 variants per species. */
export function buildFlora(def: PlanetDef): FloraSet[] {
  const pal = def.palette;
  const bark: C3 = shade(pal.cliff, 0.85);
  return speciesFor(def).map((kind) => {
    const leaf: C3 =
      kind === "crystal"
        ? [pal.water[0] * 0.7 + 0.3, pal.water[1] * 0.7 + 0.3, pal.water[2] * 0.7 + 0.3]
        : [def.scatterColor[0], def.scatterColor[1], def.scatterColor[2]];
    const variants: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 4; i++) {
      const rng = rngFromString(`${def.seed}:flora:${kind}:${i}`);
      variants.push(buildOne(rng, kind, bark, leaf));
    }
    return { kind, variants };
  });
}

/** Deterministic chipped boulders, planet-tinted. */
export function buildRocks(def: PlanetDef): THREE.BufferGeometry[] {
  const rock = landscapeColors(def).rock;
  const out: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 4; i++) {
    const rng = rngFromString(`${def.seed}:rock:${i}`);
    const geo = buildBoulder(rng, shade(rock, range(rng, .96, 1.04)), i);
    out.push(geo);
  }
  return out;
}
